import { toast } from 'sonner';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Sidebar from '../components/Sidebar';
import TopBar from '../components/TopBar';
import api from '../api';

function MyInvitations() {
  const [invitations, setInvitations] = useState([]);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [newCode, setNewCode] = useState(null);
  const { t, i18n } = useTranslation();

  useEffect(() => {
    fetchData();
    
    // Refresh data when window gains focus (when user returns to page)
    const handleFocus = () => {
      fetchData();
    };
    
    window.addEventListener('focus', handleFocus);
    
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  const fetchData = async () => {
    try {
      const [invitationsRes, userRes] = await Promise.all([
        api.get('/api/my-invitations'),
        api.get('/api/auth/me')
      ]);
      setInvitations(invitationsRes.data);
      setUser(userRes.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateCode = async () => {
    setGenerating(true);
    setNewCode(null);

    try {
      const response = await api.post('/api/generate-invitation');
      setNewCode(response.data.code);
      fetchData();
      
      setTimeout(() => {
        const codeInput = document.getElementById('new-code-input');
        if (codeInput) {
          codeInput.select();
        }
      }, 100);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al generar código');
    } finally {
      setGenerating(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('' + t('invitations.copy'));
  };

  if (loading) {
    return (
      <div className="main-layout">
        <Sidebar />
        <div className="content-area" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  const invitationsUsed = user?.invitations_used || 0;
  const invitationsAvailable = user?.invitations_available || 3;
  const canGenerateMore = invitationsUsed < invitationsAvailable;

  return (
    <div className="main-layout">
      <Sidebar />
      <div className="content-area">
        <TopBar title={`🎁 ${t('invitations.myInvitations')}`} />

        <main style={{ padding: '2rem', maxWidth: '900px', margin: '0 auto' }} className="fade-in">
      <div className="dark-card" style={{ marginBottom: '2rem', padding: '1.5rem' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '1rem' }}>
          <div style={{ flex: '1 1 200px', minWidth: 0 }}>
            <h2 style={{ fontSize: '1.5rem', fontWeight: '700', color: 'white', marginBottom: '0.5rem' }}>
              {t('invitations.myInvitations')}
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.9rem' }}>
              {t('invitations.inviteTrusted')}
            </p>
          </div>
          
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexShrink: 0 }}>
            <div style={{ textAlign: 'center', padding: '0.5rem 1rem', background: 'rgba(255,152,0,0.1)', borderRadius: '12px', border: '1px solid rgba(255,152,0,0.3)' }}>
              <div style={{ fontSize: '2rem', fontWeight: '700', color: '#FF9800', lineHeight: '1' }}>
                {invitationsAvailable - invitationsUsed}
              </div>
              <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase' }}>
                {t('invitations.available')}
              </div>
              <div style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.4)' }}>
                ({invitationsUsed}/{invitationsAvailable})
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bonus incentive banner */}
      <div style={{
        marginBottom: '2rem',
        padding: '1.5rem',
        background: 'linear-gradient(135deg, rgba(16,185,129,0.12) 0%, rgba(5,150,105,0.06) 100%)',
        border: '1.5px solid rgba(16,185,129,0.35)',
        borderRadius: '16px'
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
          <span style={{ fontSize: '2.5rem', lineHeight: 1 }}>💸</span>
          <div>
            <h3 style={{ fontSize: '1.1rem', fontWeight: '700', color: '#10b981', margin: '0 0 0.5rem 0' }}>
              {i18n.language === 'ca'
                ? 'Guanya un 1% extra per cada referit!'
                : 'Gana un 1% extra por cada referido!'}
            </h3>
            <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.9rem', lineHeight: 1.6, margin: 0 }}>
              {i18n.language === 'ca'
                ? 'Per cada persona que convidis i es converteixi en membre actiu del club Vivendius, rebràs un bonus del 1% sobre el capital que inverteixi. Aquest bonus es paga juntament amb els teus propis interessos al venciment del teu préstec.'
                : 'Por cada persona que invites y se convierta en miembro activo del club Vivendius, recibirás un bonus del 1% sobre el capital que invierta. Este bonus se paga junto con tus propios intereses al vencimiento de tu préstamo.'}
            </p>
            <div style={{ marginTop: '1rem', display: 'flex', flexWrap: 'wrap', gap: '0.75rem' }}>
              {[
                { icon: '1.', text: i18n.language === 'ca' ? 'Comparteix el teu codi' : 'Comparte tu código' },
                { icon: '2.', text: i18n.language === 'ca' ? 'El teu referit inverteix' : 'Tu referido invierte' },
                { icon: '3.', text: i18n.language === 'ca' ? 'Tu reps el 1% de bonus' : 'Tú recibes el 1% de bonus' },
              ].map((step, i) => (
                <div key={i} style={{
                  flex: '1 1 130px',
                  padding: '0.6rem 0.75rem',
                  background: 'rgba(16,185,129,0.1)',
                  borderRadius: '10px',
                  border: '1px solid rgba(16,185,129,0.2)',
                  textAlign: 'center'
                }}>
                  <span style={{ fontWeight: '700', color: '#10b981', fontSize: '0.85rem' }}>{step.icon} </span>
                  <span style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.8rem' }}>{step.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {canGenerateMore ? (
        <div className="dark-card" style={{ marginBottom: '2rem', padding: '2rem' }}>
          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: 'white', marginBottom: '1rem' }}>
            {t('invitations.generateNewCode')}
          </h3>
          <p style={{ color: 'rgba(255,255,255,0.7)', marginBottom: '1.5rem' }}>
            {t('invitations.codeOnceUse')}
          </p>
          
          <button
            onClick={handleGenerateCode}
            disabled={generating}
            style={{
              padding: '1rem 2rem',
              background: generating ? '#9ca3af' : 'linear-gradient(135deg, #FF9800 0%, #ff6f00 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              fontWeight: '700',
              cursor: generating ? 'not-allowed' : 'pointer'
            }}
            data-testid="generate-code-btn"
          >
            {generating ? t('invitations.generating') : t('invitations.generateCode')}
          </button>

          {newCode && (
            <div style={{
              marginTop: '1.5rem',
              padding: '1.5rem',
              background: 'rgba(0,122,51,0.1)',
              border: '2px solid #007a33',
              borderRadius: '12px'
            }}>
              <p style={{ fontSize: '0.875rem', color: '#34d399', marginBottom: '0.75rem' }}>
                ✓ {t('invitations.codeGenerated')}
              </p>
              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                <input
                  id="new-code-input"
                  readOnly
                  value={newCode}
                  style={{
                    flex: '1 1 200px',
                    minWidth: 0,
                    padding: '0.85rem',
                    background: 'rgba(0,0,0,0.3)',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#34d399',
                    fontSize: '1.25rem',
                    fontWeight: '700',
                    fontFamily: 'monospace',
                    textAlign: 'center'
                  }}
                  data-testid="new-code-display"
                />
                <button
                  onClick={() => copyToClipboard(newCode)}
                  style={{
                    padding: '0.85rem 1.25rem',
                    background: 'rgba(255,255,255,0.1)',
                    border: 'none',
                    borderRadius: '8px',
                    color: 'white',
                    fontWeight: '600',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap'
                  }}
                >
                  📋 {t('invitations.copy')}
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="dark-card" style={{ marginBottom: '2rem', padding: '2rem', textAlign: 'center' }}>
          <p style={{ fontSize: '1.25rem', color: '#9ca3af', fontWeight: '600', marginBottom: '1rem' }}>
            {t('invitations.noInvitationsLeft')}
          </p>
          <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.9rem', lineHeight: 1.6 }}>
            {i18n.language === 'ca'
              ? 'Necessites més invitacions? Escriu-nos a'
              : 'Necesitas más invitaciones? Escríbenos a'}
            {' '}
            <a href="mailto:info@vivendius.cat" style={{ color: '#FF9800', fontWeight: '600', textDecoration: 'none' }}>
              info@vivendius.cat
            </a>
          </p>
        </div>
      )}

      <div>
        <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: 'white', marginBottom: '1rem' }}>
          {t('invitations.myCodes')} ({invitations.length})
        </h3>

        {invitations.length === 0 ? (
          <div className="dark-card" style={{ padding: '2rem', textAlign: 'center' }}>
            <p style={{ color: 'rgba(255,255,255,0.5)' }}>
              {t('invitations.noCodesYet')}
            </p>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '1rem' }}>
            {invitations.map((inv) => (
              <div key={inv._id} className="dark-card" style={{ padding: '1.25rem' }} data-testid={`code-${inv._id}`}>
                <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: '0.75rem' }}>
                  <div style={{ flex: '1 1 200px', minWidth: 0 }}>
                    <div style={{ fontFamily: 'monospace', fontSize: '1.1rem', fontWeight: '700', color: inv.status === 'used' ? '#616161' : '#34d399', wordBreak: 'break-all' }}>
                      {inv.code}
                    </div>
                    <div style={{ fontSize: '0.8125rem', color: 'rgba(255,255,255,0.6)', marginTop: '0.5rem' }}>
                      {new Date(inv.created_at).toLocaleDateString('ca-ES')} • Usos: {inv.current_uses}/{inv.max_uses}
                    </div>
                    {inv.used_by && inv.used_by.length > 0 && (
                      <div style={{ marginTop: '0.5rem', fontSize: '0.8125rem', color: 'rgba(255,255,255,0.8)' }}>
                        {t('invitations.usedBy')}: <strong>{inv.used_by.map(u => typeof u === 'string' ? u : `${u.nombre} (${u.telefono})`).join(', ')}</strong>
                      </div>
                    )}
                  </div>
                  <span style={{
                    padding: '0.5rem 1rem',
                    background: inv.status === 'used' ? 'rgba(97,97,97,0.2)' : 'rgba(0,122,51,0.2)',
                    color: inv.status === 'used' ? '#9ca3af' : '#34d399',
                    borderRadius: '20px',
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    height: 'fit-content'
                  }}>
                    {inv.status === 'used' ? t('invitations.used') : t('invitations.active')}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      </main>
      </div>
    </div>
  );
}

export default MyInvitations;
